import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "./app/AppLayout.js";
import { lazy, Suspense } from "react";
import { LoadingState, ThemeProvider } from "@yk/ui";

// ─── Lazy page imports ────────────────────────────────────────────────────────
const LoginPage = lazy(() =>
  import("./routes/login.js").then((m) => ({ default: m.LoginPage })),
);
const SuperadminLoginPage = lazy(() =>
  import("./routes/superadmin-login.js").then((m) => ({
    default: m.SuperadminLoginPage,
  })),
);
const OAuthCallbackPage = lazy(() =>
  import("./routes/auth.callback.js").then((m) => ({
    default: m.OAuthCallbackPage,
  })),
);
const ForbiddenPage = lazy(() =>
  import("./routes/403.js").then((m) => ({ default: m.ForbiddenPage })),
);
const Dashboard = lazy(() =>
  import("./routes/dashboard.js").then((m) => ({ default: m.Dashboard })),
);
const AdminPage = lazy(() =>
  import("./routes/admin.js").then((m) => ({ default: m.AdminPage })),
);
const UsersPage = lazy(() =>
  import("./routes/users.js").then((m) => ({ default: m.UsersPage })),
);
const ConfigPage = lazy(() =>
  import("./routes/config.js").then((m) => ({ default: m.ConfigPage })),
);
const EscalationsPage = lazy(() =>
  import("./routes/escalations.js").then((m) => ({
    default: m.EscalationsPage,
  })),
);
const InventoryPage = lazy(() =>
  import("./routes/inventory.js").then((m) => ({ default: m.InventoryPage })),
);
const MaintenancePage = lazy(() =>
  import("./routes/maintenance.js").then((m) => ({
    default: m.MaintenancePage,
  })),
);
const PlatformPage = lazy(() =>
  import("./routes/platform.js").then((m) => ({ default: m.PlatformPage })),
);
const ReportsPage = lazy(() =>
  import("./routes/reports.js").then((m) => ({ default: m.ReportsPage })),
);
const TechniciansPage = lazy(() =>
  import("./routes/technicians.js").then((m) => ({
    default: m.TechniciansPage,
  })),
);
const AssetsPage = lazy(() =>
  import("./routes/assets.index.js").then((m) => ({ default: m.AssetsPage })),
);
const AssetListPage = lazy(() =>
  import("./routes/assets.list.js").then((m) => ({
    default: m.AssetListPage,
  })),
);
const AssetDetailPage = lazy(() =>
  import("./routes/assets.$id.js").then((m) => ({
    default: m.AssetDetailPage,
  })),
);
const AssetVerificationsPage = lazy(() =>
  import("./routes/verifications.index.js").then((m) => ({
    default: m.AssetVerificationsPage,
  })),
);
const AmcPage = lazy(() =>
  import("./routes/amc.js").then((m) => ({ default: m.AmcPage })),
);
const AlertsPage = lazy(() =>
  import("./routes/notifications.index.js").then((m) => ({
    default: m.AlertsPage,
  })),
);
const AssetVerificationDetailPage = lazy(() =>
  import("./routes/verifications.$id.js").then((m) => ({
    default: m.AssetVerificationDetailPage,
  })),
);
const AssetReplacementsPage = lazy(() =>
  import("./routes/asset-replacements.index.js").then((m) => ({
    default: m.AssetReplacementsPage,
  })),
);
const VerificationReportsPage = lazy(() =>
  import("./routes/verification-reports.index.js").then((m) => ({
    default: m.VerificationReportsPage,
  })),
);
const CollectiveCasesPage = lazy(() =>
  import("./routes/collective-cases.index.js").then((m) => ({
    default: m.CollectiveCasesPage,
  })),
);
const CollectiveCaseDetailPage = lazy(() =>
  import("./routes/collective-cases.$id.js").then((m) => ({
    default: m.CollectiveCaseDetailPage,
  })),
);
const ComplaintsPage = lazy(() =>
  import("./routes/complaints.index.js").then((m) => ({
    default: m.ComplaintsPage,
  })),
);
const ComplaintDetailPage = lazy(() =>
  import("./routes/complaints.$id.js").then((m) => ({
    default: m.ComplaintDetailPage,
  })),
);
const CustomersPage = lazy(() =>
  import("./routes/customers.index.js").then((m) => ({
    default: m.CustomersPage,
  })),
);
const CustomerDetailPage = lazy(() =>
  import("./routes/customers.$id.js").then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const PropertiesPage = lazy(() =>
  import("./routes/properties.index.js").then((m) => ({
    default: m.PropertiesPage,
  })),
);
const PropertyDetailPage = lazy(() =>
  import("./routes/properties.$id.js").then((m) => ({
    default: m.PropertyDetailPage,
  })),
);
const AssetUnitPage = lazy(() =>
  import("./routes/units.$id.js").then((m) => ({ default: m.AssetUnitPage })),
);

function PageLoader(): React.JSX.Element {
  return <LoadingState label="Loading…" />;
}

function SuspenseWrapper({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * Root entry. HRMS is configured with `http://localhost:5180` (origin, no path)
 * as the allowed redirect_uri, so after login it returns the user to
 * `/?code=…`. When an OAuth `code`/`error` is present we run the public OAuth
 * callback handler (it must NOT go through AppLayout's AuthGate, since the user
 * isn't signed in yet). Otherwise this is a normal landing → go to /dashboard.
 */
function RootEntry(): React.JSX.Element {
  const params = new URLSearchParams(window.location.search);
  if (params.has("code") || params.has("error")) {
    return (
      <SuspenseWrapper>
        <OAuthCallbackPage />
      </SuspenseWrapper>
    );
  }
  return <Navigate to="/dashboard" replace />;
}

export const router = createBrowserRouter([
  // ── Public routes ──────────────────────────────────────────────────────────
  {
    path: "/login",
    element: (
      <ThemeProvider>
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      </ThemeProvider>
    ),
  },
  {
    path: "/superadmin/login",
    element: (
      <ThemeProvider>
        <SuspenseWrapper>
          <SuperadminLoginPage />
        </SuspenseWrapper>
      </ThemeProvider>
    ),
  },
  {
    path: "/403",
    element: (
      <SuspenseWrapper>
        <ForbiddenPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/auth/callback",
    element: (
      <SuspenseWrapper>
        <OAuthCallbackPage />
      </SuspenseWrapper>
    ),
  },
  // Root: public — handles the HRMS `/?code=…` redirect, else → /dashboard.
  {
    path: "/",
    element: <RootEntry />,
  },

  // ── Protected layout route — wraps all app routes ──────────────────────────
  {
    element: <AppLayout />,
    children: [
      {
        path: "/dashboard",
        element: (
          <SuspenseWrapper>
            <Dashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/admin",
        element: (
          <SuspenseWrapper>
            <AdminPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/users",
        element: (
          <SuspenseWrapper>
            <UsersPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/config",
        element: (
          <SuspenseWrapper>
            <ConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/escalations",
        element: (
          <SuspenseWrapper>
            <EscalationsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/inventory",
        element: (
          <SuspenseWrapper>
            <InventoryPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/maintenance",
        element: (
          <SuspenseWrapper>
            <MaintenancePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/platform",
        element: (
          <SuspenseWrapper>
            <PlatformPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/reports",
        element: (
          <SuspenseWrapper>
            <ReportsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/technicians",
        element: (
          <SuspenseWrapper>
            <TechniciansPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/assets",
        element: (
          <SuspenseWrapper>
            <AssetsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/assets/list",
        element: (
          <SuspenseWrapper>
            <AssetListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/assets/:id",
        element: (
          <SuspenseWrapper>
            <AssetDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/units/:id",
        element: (
          <SuspenseWrapper>
            <AssetUnitPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/asset-verifications",
        element: (
          <SuspenseWrapper>
            <AssetVerificationsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/amc",
        element: (
          <SuspenseWrapper>
            <AmcPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/alerts",
        element: (
          <SuspenseWrapper>
            <AlertsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/asset-verifications/:id",
        element: (
          <SuspenseWrapper>
            <AssetVerificationDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/asset-replacements",
        element: (
          <SuspenseWrapper>
            <AssetReplacementsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/verification-reports",
        element: (
          <SuspenseWrapper>
            <VerificationReportsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/collective-cases",
        element: (
          <SuspenseWrapper>
            <CollectiveCasesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/collective-cases/:id",
        element: (
          <SuspenseWrapper>
            <CollectiveCaseDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/complaints",
        element: (
          <SuspenseWrapper>
            <ComplaintsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/complaints/:id",
        element: (
          <SuspenseWrapper>
            <ComplaintDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/customers",
        element: (
          <SuspenseWrapper>
            <CustomersPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/customers/:id",
        element: (
          <SuspenseWrapper>
            <CustomerDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/properties",
        element: (
          <SuspenseWrapper>
            <PropertiesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/properties/:id",
        element: (
          <SuspenseWrapper>
            <PropertyDetailPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
]);
